import React, { useState, useMemo, useCallback } from "react";
import { useSnackBar } from "../../../hooks/useSnackBar";
import "./AdminShipments.css";

// components imports
import TicketCreateModal from "./Components/TicketCreateModal/TicketCreateModal";
import InvoiceModal from "../../../Components/InvoiceModal/InvoiceModal";
import AdminInvoiceWorkbench, {
  useInvoiceWritesInFlight,
} from "../../../Components/AdminInvoiceWorkbench/AdminInvoiceWorkbench";
import ViewSwitchFab from "../../../Components/AdminInvoiceWorkbench/Components/ViewSwitchFab";
import Confirm from "../../../Components/Popups/Confirm";
import Tooltip from "../../../Components/Tooltip/Tooltip";
import BoltTable from "../../../Components/BoltTable/BoltTable";
import StatusIcons from "../../../Components/StatusIcons/StatusIcons";
import CircularLoader from "../../../Components/CircularLoader/CircularLoader";
import TabsComponent from "../../../Components/Common/TabsComponent";

// apis imports
import {
  fetchAllShipmentsAdmin,
  cancelShipment,
} from "../../../services/AdminServices/ShipmentsAdmin/carriers.services";
import { confirmOneTimeShipperShipment } from "../../../services/admin.services";

// helpers imports
import { convertTime } from "../../../helpers/helpers";

// library imports
import { useNavigate } from "react-router-dom";
import {
  MdOutlineCancel,
  MdComment,
  MdOutlineStickyNote2,
  MdCheckCircle,
  MdLocalShipping,
  MdReceiptLong,
  MdPayments,
  MdAccountBalance,
} from "react-icons/md";
import { FaTruckFast } from "react-icons/fa6";
import { PiExportLight } from "react-icons/pi";
import {
  carrierFilterOptions,
  dutiesFilterOptions,
  invoiceBillFilterOptions,
  invoiceStatusFilterOptions,
  shipmentStatusFilterOptions,
} from "../../../constants/filterOptions";
import BulkActionBar, {
  BulkActionButton,
} from "../../../Components/BoltTable/Components/BulkActionBar";
import { adminShipmentExport } from "./adminShipmentExport";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const AdminShipments = () => {
  const { t } = useTranslation("admin");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [invoiceModal, setInvoiceModal] = useState(false);
  // Which design the open invoice shows. Only ever one modal is mounted: the
  // Classic view is the real production InvoiceModal, whose body positions
  // itself as the modal, so it cannot be nested inside the Modern shell.
  // Defaults to classic — the new screen is opt-in until it has been proven
  // in a browser, so nobody lands on it unexpectedly on a live invoice.
  const [invoiceView, setInvoiceView] = useState("classic");
  // Remount key for the Classic InvoiceModal. InvoiceModal fetches only when
  // its `data` id changes (InvoiceModal.jsx:207-211), and a view switch keeps
  // the same id — so without a remount, coming back from Modern showed the
  // snapshot taken at row click, and a Classic Modify seeded from it posted
  // stale absolutes over a Modern write. Bumped on every switch to Classic.
  const [classicInvoiceKey, setClassicInvoiceKey] = useState(0);
  // Invoice writes (any non-GET) still in flight while an invoice is open, in
  // either view. The view switch is locked while this is non-zero, so neither
  // view can load a copy of the shipment from before a save lands.
  const invoiceWritesInFlight = useInvoiceWritesInFlight(!!invoiceModal);
  const [cancelingShipment, setCancelingShipment] = useState(false);
  const [tab, setTab] = useState("all");
  const [ticketCreateModal, setTicketCreateModal] = useState(false);
  const [confirmingShipmentId, setConfirmingShipmentId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const Navigate = useNavigate();

  const setSnack = useSnackBar();

  const defaultWidth = "200px";

  // -----------------------------
  // Helpers (memo-safe)
  // -----------------------------
  const isCancelled = useCallback(
    (row) => row?.shipment_status?.toLowerCase() === "cancelled",
    [],
  );

  const hasOpenTicket = useCallback((row) => {
    const status = row?.tickets?.[0]?.status;
    return ["Open", "Dispute", "Disputed"].includes(status);
  }, []);

  const calculateServiceChargePercentage = useCallback(
    (total, serviceCharge) => {
      const totalFloat = parseFloat(total ?? 0);
      const serviceChargeFloat = parseFloat(serviceCharge ?? 0);

      const originalPrice = totalFloat - serviceChargeFloat;

      if (!originalPrice) return "0.0";

      const serviceChargePercentage =
        (serviceChargeFloat / originalPrice) * 100;
      return serviceChargePercentage.toFixed(1);
    },
    [],
  );

  // -----------------------------
  // API Handlers
  // -----------------------------
  const handleCancelShipment = useCallback(async (id) => {
    setCancelingShipment(true);

    const response = await cancelShipment(id);

    if (!response.error) {
      setShipments((prev) => {
        return {
          ...prev,
          data: prev?.data?.map((shipment) => {
            if (shipment?.id === id) {
              return {
                ...shipment,
                shipment_status: response?.data?.result?.shipment_status,
                shipment_histories: [
                  ...shipment?.shipment_histories,
                  response?.data?.result?.shipment_histories,
                ],
              };
            }
            return shipment;
          }),
        };
      });

      setSnack((s) => ({
        ...s,
        open: true,
        text: i18n.t("admin:shipments.shipmentCancelledSuccessfully"),
        severity: "success",
      }));

      setConfirmCancel(false);
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: i18n.t("admin:shipments.couldntCancelThisShipment"),
        severity: "error",
      }));
    }

    setCancelingShipment(false);
  }, [setSnack]);

  const handleConfirmOneTimeShipperShipment = useCallback(async (id) => {
    if (!id) return;

    setConfirmingShipmentId(id);

    try {
      const response = await confirmOneTimeShipperShipment(id);

      if (!response?.error) {
        setSnack((s) => ({
          ...s,
          open: true,
          text: i18n.t("admin:shipments.shipmentConfirmedSuccessfully"),
          severity: "success",
        }));

        getAllShipments(1, 20, "");
      } else {
        setSnack((s) => ({
          ...s,
          open: true,
          text: i18n.t("admin:shipments.failedToConfirmShipment"),
          severity: "error",
        }));
      }
    } catch {
      setSnack((s) => ({
        ...s,
        open: true,
        text: i18n.t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    } finally {
      setConfirmingShipmentId(null);
    }
  }, [setSnack]);

  const handleTabChange = useCallback((event, newValue) => {
    setTab(newValue);
  }, []);

  const getAllShipments = useCallback(
    async (page, perPage, filter, nestedSearch) => {
      const response = await fetchAllShipmentsAdmin(
        tab,
        page,
        perPage,
        filter,
        nestedSearch,
      );

      if (!response.error) {
        setShipments(response?.data?.result);
      } else {
        setSnack((s) => ({
          ...s,
          open: true,
          text: i18n.t("common:messages.somethingWentWrong"),
          severity: "error",
        }));
      }
    },
    [tab, setSnack],
  );

  const handleTicketOpen = useCallback(
    (row) => {
      if (row?.tickets?.length > 0) {
        Navigate({
          pathname: "/tickets-admin",
          search: `?ticket_no=${row?.tickets[0]?.ticket_no}`,
        });
      } else {
        setTicketCreateModal(row);
      }
    },
    [Navigate],
  );

  const getInternationalData = (international) => {
    if (Array.isArray(international)) {
      return international[0] || null;
    }

    return international && typeof international === "object"
      ? international
      : null;
  };

  const getDutiesStatus = useCallback((shipment) => {
    const internationalData = getInternationalData(shipment?.international);

    // Domestic shipment OR receiver-paid duties
    if (!internationalData || internationalData?.duties_by !== "shipper") {
      return "-";
    }

    return Number(shipment?.is_duty_paid) === 0 ? "Unpaid" : "Paid";
  }, []);

  const columns = useMemo(() => {
    return [
      {
        id: 0,
        label: "#",
        render: (row) => <>{row?.id ?? "-"}</>,
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 1,
        label: t("shipments.carrier"),
        field_name: "carrier_name",
        searchable: true,
        filterIcon: FaTruckFast,
        filterOptions: carrierFilterOptions,
        render: (row) => {
          const company = row?.shipping_company?.shipping_company;
          return company?.logo_url ? (
            <img src={company.logo_url} alt={company?.name || t("shipments.carrierAlt")} />
          ) : (
            (company?.name ?? "-")
          );
        },
        width: defaultWidth,
        fixed: "left",
        disabled: isCancelled,
      },
      {
        id: 2,
        label: t("shipments.client"),
        field_name: "client",
        searchable: true,
        render: (row) => <>{row?.user?.username ?? "-"}</>,
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 3,
        label: t("shipments.shipmentStatus"),
        field_name: "shipment_status",
        searchable: true,
        filterIcon: MdLocalShipping,
        filterOptions: shipmentStatusFilterOptions,
        render: (row) => (
          <StatusIcons status={row?.shipment_status} showTitle={true} />
        ),
        width: "150px",
        disabled: isCancelled,
      },
      {
        id: 4,
        label: t("shipments.pickupId"),
        field_name: "PickupID",
        searchable: true,
        render: (row) => <>{row?.PickupID ?? "-"}</>,
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 5,
        label: t("shipments.type"),
        render: (row) => (
          <>
            {!row?.shipment_type
              ? "-"
              : row.shipment_type === "trucker"
                ? t("shipments.carrier")
                : row.shipment_type}
          </>
        ),
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 6,
        label: t("shipments.boltTracking"),
        render: (row) => <>{row?.tracking_id ?? "-"}</>,
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 7,
        label: t("shipments.carrierTracking"),
        field_name: "tracking_id",
        searchable: true,
        render: (row) => (
          <>
            {tab === 2 ? (row?.id ?? "-") : (row?.shipment_tracking_id ?? "-")}
          </>
        ),
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 8,
        label: t("shipments.customer"),
        field_name: "tattension",
        searchable: true,
        render: (row) => <>{row?.tattension ?? "-"}</>,
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 9,
        label: t("shipments.weight"),
        render: (row) => (
          <>
            {row?.total_weight
              ? `${row.total_weight} ${row?.unit === "Imperial" ? "Lb" : "Kg"}`
              : ""}
          </>
        ),
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 10,
        label: t("shipments.markup"),
        render: (row) => {
          const serviceCharge = Number(row?.service_charge || 0);

          if (serviceCharge <= 0) return null;

          const markup =
            row?.markup > 0
              ? parseFloat(row.markup)
              : calculateServiceChargePercentage(row?.Total, serviceCharge);

          return (
            <>
              {serviceCharge.toFixed(2)} ({markup} %)
            </>
          );
        },
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 11,
        label: t("shipments.total"),
        render: (row) => {
          const charges = row?.shipment_charges || [];

          return (
            <div>
              <div
                className={
                  charges.length > 1 ? "shipments__total_updatedamount" : ""
                }
              >
                {row.Total ?? "-"}
                <sup>{charges.length > 1 ? `*${charges.length}` : ""}</sup>
              </div>

              <div className="shipments__total_prevamount">
                {charges.length > 1 ? charges[charges.length - 2]?.total : ""}
              </div>
            </div>
          );
        },
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 12,
        label: t("shipments.invoice"),
        field_name: "admin_invoice_status",
        searchable: true,
        filterIcon: MdReceiptLong,
        filterOptions: invoiceStatusFilterOptions,
        render: (row) => (
          <StatusIcons
            status={
              row?.admin_invoice_status === null
                ? "Unpaid"
                : row?.admin_invoice_status
            }
            noBg
            noDot
          />
        ),
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 13,
        label: t("shipments.billing"),
        field_name: "is_bill_verified",
        searchable: true,
        filterIcon: MdPayments,
        filterOptions: invoiceBillFilterOptions,
        render: (row) => (
          <StatusIcons
            status={row?.is_bill_verified == 0 ? "Unverified" : "Verified"}
            noBg
            noDot
          />
        ),
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 14,
        label: t("shipments.duties"),
        field_name: "duty_payment_status",
        searchable: true,
        filterIcon: MdAccountBalance,
        filterOptions: dutiesFilterOptions,
        render: (row) => (
          <StatusIcons status={getDutiesStatus(row)} noBg noDot />
        ),
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 15,
        label: t("shipments.createdAt"),
        render: (row) => <>{convertTime(row?.created_at)}</>,
        width: defaultWidth,
        disabled: isCancelled,
      },
      {
        id: 16,
        label: t("shipments.actions"),
        render: (row) => {
          const isOneTimeShipper =
            tab === "oneTimeShipper" &&
            row?.shipment_type === "oneTimeShipper" &&
            row?.shipment_status?.toLowerCase() === "creation";

          const isConfirming = confirmingShipmentId === row?.id;

          return (
            <div className="table_action__wrap">
              {isOneTimeShipper && (
                <Tooltip title={t("shipments.confirmShipment")}>
                  <div
                    className="table_action__btn"
                    style={{
                      background: "#0043CE",
                      color: "white",
                      cursor: isConfirming ? "not-allowed" : "pointer",
                      opacity: isConfirming ? 0.6 : 1,
                      minWidth: "65px",
                      width: "65px",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => {
                      if (!isConfirming) {
                        handleConfirmOneTimeShipperShipment(row.id);
                      }
                    }}
                  >
                    {isConfirming ? (
                      <CircularLoader size={10} color="text-white" />
                    ) : (
                      <MdCheckCircle size={10} />
                    )}
                    <span style={{ whiteSpace: "nowrap" }}>{t("common:actions.confirm")}</span>
                  </div>
                </Tooltip>
              )}

              <Tooltip title={t("shipments.tickets")}>
                <div
                  className="table_action__btn"
                  onClick={() => handleTicketOpen(row)}
                  style={{
                    background: hasOpenTicket(row) ? "red" : "none",
                    color: hasOpenTicket(row) ? "white" : "inherit",
                  }}
                >
                  <MdComment size={10} />
                  {t("shipments.ticket")}
                </div>
              </Tooltip>

              <Tooltip title={t("shipments.viewInvoice")}>
                <div
                  className="table_action__btn"
                  onClick={() => {
                    setInvoiceView("classic");
                    setInvoiceModal(row?.id);
                  }}
                >
                  <MdOutlineStickyNote2 size={10} />
                  {t("shipments.invoice")}
                </div>
              </Tooltip>

              <Tooltip title={t("shipments.cancelShipment")}>
                <div
                  className={`table_action__btn ${
                    isCancelled ? "table_action__btn__disabled" : ""
                  }`}
                  onClick={() => setConfirmCancel(row.id)}
                >
                  <MdOutlineCancel size={10} />
                  {t("common:actions.cancel")}
                </div>
              </Tooltip>
            </div>
          );
        },
        width: "350px",
        fixed: "right",
        disabled: isCancelled,
      },
    ];
  }, [
    tab,
    confirmingShipmentId,
    calculateServiceChargePercentage,
    handleConfirmOneTimeShipperShipment,
    handleTicketOpen,
    isCancelled,
    hasOpenTicket,
    t,
  ]);

  return (
    <div
      className="dispatcher__screen"
      style={{ maxWidth: "100%", padding: "0px 5px" }}
    >

      <Confirm
        open={confirmCancel}
        setOpen={setConfirmCancel}
        handleConfirm={() => handleCancelShipment(confirmCancel)}
        text={t("shipments.doYouWantToCancel")}
        loader={cancelingShipment}
      />

      <InvoiceModal
        key={classicInvoiceKey}
        open={!!invoiceModal && invoiceView === "classic"}
        data={invoiceModal}
        setData={setInvoiceModal}
        setOpen={setInvoiceModal}
        setShipments={setShipments}
        getAllShipments={getAllShipments}
        type="editable"
        setSnack={setSnack}
      />

      <AdminInvoiceWorkbench
        open={!!invoiceModal && invoiceView === "modern"}
        data={invoiceModal}
        setOpen={() => setInvoiceModal(false)}
        setShipments={setShipments}
        getAllShipments={getAllShipments}
        setSnack={setSnack}
        switchBlocked={invoiceWritesInFlight > 0}
        onSwitchToClassic={() => {
          if (invoiceWritesInFlight > 0) return;
          setClassicInvoiceKey((k) => k + 1);
          setInvoiceView("classic");
        }}
      />

      {/* The way back, while the production invoice is open. It floats above
          the modal rather than living inside it, so EditableInvoice stays
          untouched. */}
      {!!invoiceModal && invoiceView === "classic" && (
        <ViewSwitchFab
          disabled={invoiceWritesInFlight > 0}
          onSwitch={() => {
            if (invoiceWritesInFlight > 0) return;
            setInvoiceView("modern");
          }}
        />
      )}

      <TicketCreateModal
        open={!!ticketCreateModal}
        setOpen={setTicketCreateModal}
        previousData={ticketCreateModal}
        setSnack={setSnack}
      />

      <TabsComponent
        items={[
          { label: t("shipments.allCarrier"), value: "all" },
          { label: t("shipments.hajexCarriers"), value: "Hajex" },
          { label: t("shipments.boltSelfCarriers"), value: "Trucker" },
          { label: t("shipments.customCarriers"), value: "Custom" },
          { label: t("shipments.oneTimeShipper"), value: "oneTimeShipper" },
        ]}
        tab={tab}
        handleTabChange={handleTabChange}
      />

      <div className="custom__table_size1">
        <BoltTable
          tableTitle={t("shipments.shipments")}
          tableId="adminShipmentsTable"
          message={t("shipments.noShipmentsAvailableToDisplay")}
          searchPlaceHolder={t("shipments.searchShipments")}
          getAllData={getAllShipments}
          data={shipments}
          columns={columns}
          refresh={tab}
          selectable={true}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
        <BulkActionBar
          count={selectedRows.length}
          onClear={() => setSelectedRows([])}
        >
          <BulkActionButton
            label={t("shipments.exportSelected")}
            onClick={() =>
              adminShipmentExport(shipments, selectedRows, setSnack)
            }
            icon={PiExportLight}
          />
        </BulkActionBar>
      </div>
    </div>
  );
};

export default AdminShipments;
